import { useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Wallet, Package, Zap, Shield, Crown, Sparkles } from "lucide-react";

/**
 * WalletPacks — carteira + packs de créditos (boost + AI screening).
 * Recebe:
 *   - wallet: { express, silver, gold, ai_screening }
 *   - packs:  [ { pack, label, amount, credits, tagline, kind, description } ]
 *   - onGoToScreening?: () => void  (opcional; chama para ir para tab Triagem IA)
 */
export const WalletPacks = ({ wallet, packs, onGoToScreening }) => {
  const [buying, setBuying] = useState(null);

  const buy = async (pack) => {
    setBuying(pack);
    try {
      const { data } = await api.post("/payments/checkout-pack", { pack, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatErr(e));
      setBuying(null);
    }
  };

  const boostPacks = (packs || []).filter(p => p.kind !== "ai_screening");
  const aiPacks = (packs || []).filter(p => p.kind === "ai_screening");

  const walletTiles = [
    { key: "express", label: "Créditos EXPRESS", icon: Zap, cls: "text-orange-500" },
    { key: "silver",  label: "Créditos SILVER",  icon: Shield, cls: "text-sky-500" },
    { key: "gold",    label: "Créditos GOLD",    icon: Crown, cls: "text-amber-500" },
    { key: "ai_screening", label: "Análises IA", icon: Sparkles, cls: "text-fuchsia-500" },
  ];

  return (
    <div className="mt-10">
      {/* Carteira resumo */}
      <div data-testid="wallet-card" className="bg-white rounded-2xl border border-black/5 p-5 sm:p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-5 h-5 text-amber-500" strokeWidth={1.8} />
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">A tua carteira</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {walletTiles.map(t => {
            const Ic = t.icon;
            return (
              <div key={t.key} className="flex items-center gap-3 bg-slate-50/60 rounded-xl px-4 py-3">
                <Ic className={`w-5 h-5 ${t.cls}`} strokeWidth={1.8} />
                <div>
                  <p data-testid={`wallet-${t.key}`} className="font-heading font-black tracking-tighter text-2xl text-slate-900">
                    {wallet?.[t.key] || 0}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">{t.label}</p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 mt-4">
          Usa os créditos EXPRESS/SILVER/GOLD no separador Vagas para destacar sem pagamento adicional.
          {onGoToScreening && (
            <> Ou usa as análises IA no {" "}
              <button className="text-fuchsia-600 font-bold underline" onClick={onGoToScreening}>separador Triagem IA</button>.</>
          )}
        </p>
      </div>

      {/* Boost packs */}
      {boostPacks.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-4 flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" /> Packs de destaques — poupa até 37%
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
            {boostPacks.map((p) => (
              <div key={p.pack} data-testid={`pack-card-${p.pack}`} className="bg-white rounded-2xl border border-black/5 p-6 flex flex-col hover:border-sky-300 transition-colors">
                <p className="font-heading font-black tracking-tighter text-lg text-slate-900">{p.label}</p>
                <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
                <p className="font-heading font-black tracking-tighter text-3xl text-slate-900 mt-3">{(p.amount / 100).toFixed(0)}€</p>
                <Button data-testid={`buy-pack-${p.pack}`} disabled={buying === p.pack} onClick={() => buy(p.pack)}
                  className="rounded-full bg-slate-900 hover:bg-slate-800 mt-4">
                  {buying === p.pack ? "A redirecionar..." : "Comprar pack"}
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
